import { NEXT_ACTION_LABELS, type NextAction, type TagDTO } from '@hearth/shared';
import { clsx } from 'clsx';
import { Ban, Flame, X, Zap } from 'lucide-react';
import { useLookups } from '../../hooks/useMeta';
import { colorClasses } from '../../lib/colors';
import { EMPTY } from '../../lib/format';
import { Badge } from '../ui/Badge';
import { Tooltip } from '../ui/Tooltip';

type SizeProp = { size?: 'sm' | 'md'; className?: string };

function Missing() {
  return <span className="text-slate-400">{EMPTY}</span>;
}

export type TierBadgeProps = SizeProp & { tierKey: string | null | undefined };

/**
 * Lead tier from meta (label + color). The top tier (highest minScore — "Hot" by default) renders solid
 * with a flame so it stands out in dense tables; the others are soft.
 */
export function TierBadge({ tierKey, size, className }: TierBadgeProps) {
  const { meta, lookups } = useLookups();
  const tier = lookups.tierByKey(tierKey);
  if (!tier) return <Missing />;
  const topMinScore = Math.max(...(meta?.tiers ?? []).map((t) => t.minScore));
  const isTop = tier.minScore === topMinScore;
  return (
    <Badge
      color={tier.color}
      variant={isTop ? 'solid' : 'soft'}
      icon={isTop ? Flame : undefined}
      dot={!isTop}
      size={size}
      title={tier.description ?? `${tier.label}: score ${tier.minScore}+`}
      className={className}
    >
      {tier.label}
    </Badge>
  );
}

export type StageBadgeProps = SizeProp & { stageId?: string | null; stageKey?: string | null };

/** Pipeline stage by id or key. */
export function StageBadge({ stageId, stageKey, size, className }: StageBadgeProps) {
  const { lookups } = useLookups();
  const stage = stageId ? lookups.stageById(stageId) : lookups.stageByKey(stageKey);
  if (!stage) return <Missing />;
  return (
    <Badge color={stage.color} dot size={size} className={className}>
      {stage.label}
    </Badge>
  );
}

export type RelationshipBadgeProps = SizeProp & { relationshipId?: string | null; relationshipKey?: string | null };

/** Relationship status; statuses that block outreach show a ban icon. */
export function RelationshipBadge({ relationshipId, relationshipKey, size, className }: RelationshipBadgeProps) {
  const { lookups } = useLookups();
  const rel = relationshipId ? lookups.relationshipById(relationshipId) : lookups.relationshipByKey(relationshipKey);
  if (!rel) return <Missing />;
  return (
    <Badge
      color={rel.color}
      icon={rel.blocksOutreach ? Ban : undefined}
      dot={!rel.blocksOutreach}
      size={size}
      title={rel.blocksOutreach ? `${rel.label} — outreach blocked` : rel.label}
      className={className}
    >
      {rel.label}
    </Badge>
  );
}

export type ScorePillProps = {
  score: number | null | undefined;
  /** Use the lead's stored tier; otherwise the tier is derived from the score. */
  tierKey?: string | null;
  /** Hide the mini bar (tight spaces). */
  compact?: boolean;
  className?: string;
};

/** 0–100 lead score with a mini bar colored by tier. */
export function ScorePill({ score, tierKey, compact, className }: ScorePillProps) {
  const { lookups } = useLookups();
  if (score === null || score === undefined) return <Missing />;
  const tier = (tierKey ? lookups.tierByKey(tierKey) : undefined) ?? lookups.tierForScore(score);
  const colors = colorClasses(tier?.color);
  const clamped = Math.max(0, Math.min(100, score));
  return (
    <span className={clsx('inline-flex items-center gap-1.5', className)} title={tier ? `Score ${score} · ${tier.label}` : `Score ${score}`}>
      <span className={clsx('w-6 text-right text-[13px] font-semibold tabular', colors.text)}>{Math.round(score)}</span>
      {!compact && (
        <span aria-hidden className="h-1.5 w-10 overflow-hidden rounded-full bg-slate-200">
          <span className={clsx('block h-full rounded-full', colors.dot)} style={{ width: `${clamped}%` }} />
        </span>
      )}
    </span>
  );
}

/** Visual weight per next action: urgent ones in the brand color, blockers red, passive ones muted. */
const NEXT_ACTION_COLORS: Record<NextAction, string> = {
  do_not_contact: 'red',
  review_duplicate: 'amber',
  complete_overdue_task: 'orange',
  respond_to_reply: 'orange',
  prepare_quote: 'violet',
  send_quote_follow_up: 'violet',
  contact_now: 'orange',
  call: 'blue',
  follow_up: 'blue',
  research_contact: 'sky',
  wait_until_scheduled: 'slate',
  nurture: 'teal',
  no_action: 'slate',
};

export type NextActionChipProps = SizeProp & {
  action: NextAction | null | undefined;
  /** Why the rules chose this action; shown as a tooltip. */
  reason?: string | null;
};

export function NextActionChip({ action, reason, size, className }: NextActionChipProps) {
  if (!action) return <Missing />;
  const chip = (
    <Badge color={NEXT_ACTION_COLORS[action]} icon={action === 'no_action' ? undefined : Zap} size={size} className={className}>
      {NEXT_ACTION_LABELS[action]}
    </Badge>
  );
  return reason ? (
    <Tooltip content={reason}>
      <span tabIndex={0} className="inline-flex rounded-md focus-visible:outline-2 focus-visible:outline-ember-500">
        {chip}
      </span>
    </Tooltip>
  ) : (
    chip
  );
}

export type TagChipProps = SizeProp & {
  /** Resolve from meta by id… */
  tagId?: string;
  /** …or pass the tag directly. */
  tag?: Pick<TagDTO, 'name' | 'color'>;
  onRemove?: () => void;
};

export function TagChip({ tagId, tag, onRemove, size, className }: TagChipProps) {
  const { lookups } = useLookups();
  const resolved = tag ?? lookups.tagById(tagId);
  if (!resolved) return null;
  const colors = colorClasses(resolved.color);
  return (
    <span
      className={clsx(
        'inline-flex max-w-full items-center gap-1 rounded-full border px-2 font-medium whitespace-nowrap',
        size === 'md' ? 'h-6 text-xs' : 'h-5 text-2xs',
        colors.badge,
        className,
      )}
    >
      <span aria-hidden className={clsx('size-1.5 shrink-0 rounded-full', colors.dot)} />
      <span className="truncate">{resolved.name}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove tag ${resolved.name}`}
          className="-mr-1 rounded-full p-0.5 opacity-60 hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/10"
        >
          <X size={10} aria-hidden />
        </button>
      )}
    </span>
  );
}
